import React, { useEffect, useState } from 'react';
import { fetchLectureData } from '../services/googleSheets';
import EventSection from '../components/EventSection';
import DesktopLayout from '../layouts/DesktopLayout';
import MobileLayout from '../layouts/MobileLayout';
import { useConfig } from '../config/ConfigContext';
import useMediaQuery from '../hooks/useMediaQuery';
import FloatingShareButton from '../components/FloatingShareButton';
import FloatingActionButton from '../components/home/FloatingActionButton';
import { metaTagsService } from '../services/metaTagsService';

// Simple mobile detection using window width
const isMobile = () => window.innerWidth <= 768;

/**
 * New YouTube-style homepage that organizes lectures by events
 * @returns {JSX.Element}
 */
const NewHomePage = () => {
  const { config, theme, loading: configLoading, error: configError } = useConfig(); // Get theme too
  const [eventGroups, setEventGroups] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const isMobileView = useMediaQuery('(max-width: 768px)');
  
  // Debug mobile detection
  console.log('NewHomePage - isMobileView:', isMobileView, 'window.innerWidth:', window.innerWidth);
  
  // Function to group lectures by event
  const groupLecturesByEvent = (lectures) => {
    return lectures.reduce((groups, lecture) => {
      // Use event from Column A, default to 'Uncategorized' if not available
      const event = lecture.event || 'Uncategorized';
      
      if (!groups[event]) {
        groups[event] = [];
      }
      
      groups[event].push(lecture);
      return groups;
    }, {});
  };

  // Sort series by most recent lecture date (series with the newest lecture appears first)
  const sortEventNames = (eventNames, groupedLectures) => {
    // Parse date string (handles MM/DD/YYYY, YYYY-MM-DD, "Mon DD, YYYY", etc.)
    const parseDate = (dateStr) => {
      if (!dateStr) return 0;
      const d = new Date(dateStr);
      return isNaN(d.getTime()) ? 0 : d.getTime();
    };

    return eventNames.sort((a, b) => {
      // Find the most recent lecture date in each series
      const getLatestTimestamp = (eventName) => {
        const lectures = groupedLectures[eventName] || [];
        let latest = 0;
        lectures.forEach(l => {
          const ts = parseDate(l.date);
          if (ts > latest) latest = ts;
        });
        return latest;
      };
      // Most recent first (descending)
      return getLatestTimestamp(b) - getLatestTimestamp(a);
    });
  };
  
  useEffect(() => {
    const loadData = async () => {
      // Wait for config to load before fetching data
      if (configLoading || !config) {
        console.log('NewHomePage: Waiting for config to load...');
        return; 
      }
      // Check for config errors
      if (configError) {
        console.error('NewHomePage: Config loading failed:', configError);
        setError(`Failed to load configuration: ${configError.message || configError}`);
        setLoading(false);
        return;
      }

      // Ensure google_sheet_id is available
      if (!config.google_sheet_id) {
        console.error('NewHomePage: google_sheet_id is missing from config.');
        setError('Spreadsheet ID is missing in the configuration.');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        
        console.log(`NewHomePage: Fetching data for sheet ID: ${config.google_sheet_id}`); // Log the sheet ID being used
        // Fetch lecture data from Google Sheets using the dynamic sheet ID
        let lectures = await fetchLectureData(config.google_sheet_id); // Pass sheetId
        console.log('Raw lecture data:', lectures);
        
        if (!Array.isArray(lectures)) { // Keep check for non-array response
          throw new Error('No lecture data found');
        }
        
        // Group lectures by event (from Column A)
        const grouped = groupLecturesByEvent(lectures);
        console.log('Grouped by event:', grouped);
        
        // Get sorted event names
        const sortedEventNames = sortEventNames(Object.keys(grouped), grouped);
        
        // Create sorted object with groups
        const sortedGroups = {};
        sortedEventNames.forEach(eventName => {
          sortedGroups[eventName] = grouped[eventName];
        });
        
        setEventGroups(sortedGroups);
        setLoading(false);
      } catch (err) {
        console.error('Error loading lecture data:', err);
        setError('Failed to load lecture data. Please try again later.');
        setLoading(false);
      }
    };

    loadData();
  }, [config, configLoading, configError]); // Add config, configLoading, configError to dependencies

  // Update meta tags when component mounts or config changes
  useEffect(() => {
    if (config && !configLoading) {
      const logoUrl = config.logo_url || `${window.location.origin}/images/penta-lectures-logo.png`;
      const description = config.description || 'Access lecture summaries, videos, and notes from your center';
      
      metaTagsService.updateMetaTags({
        title: `${config.name || 'Penta University'} - Lecture Platform`,
        description: description,
        image: logoUrl,
        url: window.location.origin,
        type: 'website'
      });
    }

    // Cleanup function to reset meta tags when component unmounts
    return () => {
      if (config) {
        metaTagsService.resetToDefaults();
      }
    };
  }, [config, configLoading]);

  // Handle config loading/error state before rendering
  if (configLoading) {
    return <p>Loading configuration...</p>;
  }

  if (configError) {
    return <p>Error loading configuration: {configError.message || configError}</p>;
  }
  
  // Render loading state
  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        fontSize: '18px',
        color: theme.colors.primary
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ marginBottom: '16px' }}>
            Loading lecture content...
          </div>
          <div style={{
            width: '40px',
            height: '40px',
            margin: '0 auto',
            border: `4px solid ${theme.colors.lightGray}`,
            borderTopColor: theme.colors.primary,
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
          }}></div>
        </div>
      </div>
    );
  }
  
  // Render error state
  if (error) {
    return (
      <div style={{
        padding: '40px',
        textAlign: 'center',
        color: theme.colors.error
      }}>
        <h2>Error Loading Content</h2>
        <p>{error}</p>
        <button
          onClick={() => window.location.reload()}
          style={{
            backgroundColor: theme.colors.primary,
            color: 'white',
            border: 'none',
            padding: '10px 16px',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '16px',
            marginTop: '16px'
          }}
        >
          Retry
        </button>
      </div>
    );
  }
  
  // No events found
  if (Object.keys(eventGroups).length === 0) {
    return (
      <div style={{
        padding: '40px',
        textAlign: 'center'
      }}>
        <h2>No Events Found</h2>
        <p>Check back later for upcoming events.</p>
      </div>
    );
  }
  
  // Determine which layout to use based on device type
  const Layout = isMobileView ? MobileLayout : DesktopLayout;
  
  return (
    <Layout>
      {/* Add inline styles to ensure mobile font sizes work */}
      {isMobileView && (
        <style>
          {`
            @media (max-width: 768px) {
              .homepage-header-title {
                font-size: 1.75rem !important;
              }
              .mobile-layout header h1 {
                font-size: 1.75rem !important;
              }
            }
          `}
        </style>
      )}
      <div className="home-container" style={{ 
        maxWidth: '1200px', 
        margin: '0 auto',
        padding: '30px 20px',
        backgroundColor: theme.colors.background,
        minHeight: 'calc(100vh - 160px)'
      }}>
        {/* Page header */}
        <header style={{ 
          marginBottom: '40px',
          textAlign: 'center',
        }}>
          <h1 className="homepage-header-title" style={{
            color: theme.colors.headingColor || theme.colors.primary,
            fontSize: isMobileView ? '1.75rem' : '2.5rem',
            fontWeight: '700',
            marginBottom: '10px',
            lineHeight: 1.2,
            padding: '0 10px'
          }}>
            {config.name ? `${config.name} Lecture Series` : 'Lecture Series'}
          </h1>
          <p className="homepage-header-subtitle" style={{
            color: theme.colors.text
          }}>
            Explore our collection of lectures organized by events
          </p>
        </header>
        
        {/* Event sections */}
        <div className="event-sections-container" style={{ 
          display: 'flex',
          flexDirection: 'column',
          gap: '48px'
        }}>
          {Object.entries(eventGroups).map(([eventName, lectures], index) => (
            <React.Fragment key={eventName}>
              <EventSection
                eventName={eventName}
                lectures={lectures}
              />
            </React.Fragment>
          ))}
        </div>
      </div>
      <FloatingActionButton
        centerName={config?.name}
        config={config}
        theme={theme}
        showSearch={true}
      />
    </Layout>
  );
};

export default NewHomePage;
